import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, ShieldCheck } from 'lucide-react';
import api from '@/utils/api';

const RsvpEditPage = () => {
  const [params] = useSearchParams();
  const token = params.get('t');
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!token) { setError('Missing token'); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/rsvp/edit/${token}`);
        setPayload(data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Link no longer valid');
      } finally { setLoading(false); }
    })();
  }, [token]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.patch(`/rsvp/edit/${token}`, {
        email: payload.registration.email,
        phone: payload.registration.phone,
        answers: payload.registration.answers,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-700" /></div>;
  if (error) return (
    <div className="container mx-auto px-4 py-24">
      <Card className="max-w-lg mx-auto border shadow-soft">
        <CardContent className="p-10 text-center">
          <h1 className="font-display text-2xl font-bold text-primary-900 mb-2">Link no longer valid</h1>
          <p className="text-neutral-600 mb-6">{error}</p>
          <Button asChild variant="outline"><Link to="/events">Browse events</Link></Button>
        </CardContent>
      </Card>
    </div>
  );

  const r = payload.registration;
  const ev = payload.event;
  const a = r.answers || {};
  const setA = (k, v) => setPayload({ ...payload, registration: { ...r, answers: { ...a, [k]: v } } });

  return (
    <div className="container mx-auto px-4 md:px-8 py-10 max-w-2xl">
      <Link to={`/events/${ev.slug}`} className="text-sm text-neutral-500 hover:text-primary-800 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to event
      </Link>
      <h1 className="font-display text-3xl md:text-4xl font-bold text-primary-900 mb-1">Edit your RSVP</h1>
      <p className="text-neutral-600 mb-6">{ev.title} · status: <span className="font-semibold text-primary-900">{r.status}</span></p>

      {payload.payment?.payment_status === 'auto_verified_pending_review' && (
        <div
          className="mb-6 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 flex items-start gap-3"
          data-testid="rsvp-payment-pending-review-banner"
        >
          <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Payment received, awaiting host confirmation.</div>
            <div className="text-sm text-amber-800 mt-0.5">
              Your Zelle payment landed and we matched it to this RSVP. A host will confirm your seat shortly - you&apos;ll get an email with your ticket the moment they do. No further action needed from you.
            </div>
          </div>
        </div>
      )}

      <Card className="border shadow-soft">
        <CardContent className="p-6 md:p-8 space-y-5">
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label className="text-xs uppercase tracking-widest text-secondary-700">Email</Label><Input type="email" value={r.email || ''} onChange={e => setPayload({ ...payload, registration: { ...r, email: e.target.value } })} /></div>
            <div><Label className="text-xs uppercase tracking-widest text-secondary-700">Phone</Label><Input value={r.phone || ''} onChange={e => setPayload({ ...payload, registration: { ...r, phone: e.target.value } })} /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-secondary-700">About you</Label><Textarea rows={4} value={a.about_me || ''} onChange={e => setA('about_me', e.target.value)} /></div>
          <div className="grid gap-4 md:grid-cols-2">
            <div><Label className="text-xs uppercase tracking-widest text-secondary-700">City</Label><Input value={a.city || ''} onChange={e => setA('city', e.target.value)} /></div>
            <div><Label className="text-xs uppercase tracking-widest text-secondary-700">Dietary</Label><Input value={a.dietary_restrictions || ''} onChange={e => setA('dietary_restrictions', e.target.value)} /></div>
          </div>
          <div><Label className="text-xs uppercase tracking-widest text-secondary-700">Food allergies</Label><Input value={a.food_allergies || ''} onChange={e => setA('food_allergies', e.target.value)} /></div>
          <div><Label className="text-xs uppercase tracking-widest text-secondary-700">Accessibility request</Label><Input value={a.accessibility_request || ''} onChange={e => setA('accessibility_request', e.target.value)} /></div>

          <div className="flex items-center gap-3 pt-2">
            <Button onClick={save} disabled={saving} className="bg-primary-800 hover:bg-primary-900 text-white">
              <Save className="h-4 w-4 mr-2" /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
            {saved && <span className="text-sm text-success-700">Saved</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RsvpEditPage;
